import React, { useState, ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Group, Stack, Button, Tooltip, Divider, Loader, Table, LoadingOverlay, Title, Text, Popover, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import classnames from 'classnames';
import SurveyJob from '../SurveyJob/SurveyJob';
import { DropZone } from './DropZone';
import { FieldIcon } from './FieldIcon';
import { IOrgJobCardProps, Column, INode } from '../Types/BulkPricingTypes'
import { IAppState } from '../../../../lib/types/AppState/index';
import { getElementShortName } from '../../../../lib/formatters';
import { getFieldData, formatValue, handleAddMatches, handleGetMatches } from '../utils';
import { useDrop } from 'react-dnd';
import addAlert from 'ps-components/AlertToast/addAlert';

export const OrgJobCard: React.FC<IOrgJobCardProps> = (props): ReactElement => {
  const [showMatches, setShowMatches] = useState(false);
  const [columns, setColumns] = useState<Column[]>([]);
  const [popoverHovered, setPopoverHovered] = useState(false);
  const [titleHovered, setTitleHovered] = useState(false);
  const [opened, { close, open }] = useDisclosure(false);

  const masterAccountID = useSelector((state: IAppState) => state.session.organization.masterAccountId);
  const benchmarkYear = useSelector((state: IAppState) => state.session.benchmarkYear);
  const currency = useSelector((state: IAppState) => state.session.currency.code);
  const useJobCodeKey = useSelector((state: IAppState) => state.session.organization.useJobCodeKey);
  const displayIn = useSelector((state: IAppState) => state.session.monetary_display);
  
  const { job, updateMatchesInState, checkedDataCuts, setSelectedMatches, setOrgJobsDataStale, setMessages, setHasDisplayedMessages, matchDataLoading, setMatchDataLoading, selectedMatches, setCheckedDataCuts } = props;
  const { children, data, terms } = job;
  children.sort((a, b) => {
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
  })
  const matchCount: number = getFieldData('match_count_default', data, terms);
  const jobCode = getFieldData('org_job_code_default', data, terms)?.trim?.();
  const jobCodeKey = getFieldData('org_job_code_key_default', data, terms)?.trim?.();
  const jobTitle = getFieldData('org_job_title_default', data, terms)?.trim?.();
  const jobDescription: string = getFieldData('org_job_description_default', data, terms);

  const disableShowMatches = !matchCount;
  const cutIDs = checkedDataCuts.map((cut) => cut.id) as number[];
  const hasChildren = children?.length > 0
  const jobCurrency = getFieldData('currency_default', job.data, job.terms);
  const checkedDataCutCurrency = checkedDataCuts.length ? getFieldData('currency_default', checkedDataCuts[0].data, checkedDataCuts[0].terms) : null;
  const currenciesMatch = jobCurrency === checkedDataCutCurrency;

  // this is a hack until aging_code is added to org job default terms
  let agingCode: string = '';
  if (children[0]?.children[0]?.data) {
    agingCode = getFieldData('aging_code_default', children[0].children[0]?.data, children[0].terms);
  }

  /** configures org job card as a drop zone to receive the dragged data cuts */
  const [{ isOver, item }, drop] = useDrop({
    accept: 'DATA_CUTS',
    drop: (item: any) => {
      if (!item.dataCuts.length) {
        addAlert('Please select at least one data cut to drag and drop matches.', 'warning');
        return;
      };
      if (!currenciesMatch) {
        addAlert('The currency of the selected data cut(s) does not match the currency of the job.', 'warning');
        return;
      }
      handleAddMatches({ 
        setMessages, 
        setHasDisplayedMessages, 
        updateMatchesInState,
        cutIDs, 
        benchmarkYear, 
        agingCode,
        currency,
        masterAccountID,
        job,
        setMatchDataLoading,
        displayIn
      }).then(() => {
        setCheckedDataCuts([]);
      })
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      item: monitor.getItem(),
    }),
  });

  useEffect(() => {
    const viewColumns = terms.filter((term) => !term.term?.endsWith('_default') && (term.dataType === 'monetary' || term.term?.includes?.('compa_ratio'))) as Column[];
    if (viewColumns.length > 3) viewColumns.splice(3);
    const defaultColumns: Column[] = [];
    defaultColumns.push(
      terms.find((term) => term.term?.includes('org_unit')) as Column,
      terms.find((term) => term.term?.includes('org_salary_structure')) as Column,
      terms.find((term) => term.term?.includes('org_salary_grade')) as Column
    );
    defaultColumns[0].name = 'Family';
    defaultColumns[1].name = 'Structure';
    defaultColumns[2].name = 'Grade';
    const newColumns = [...defaultColumns, ...viewColumns];
    setColumns(newColumns);
  }, [terms]);

  useEffect(() => {
    if (opened) {
      const timer = setTimeout(() => {
        if (!popoverHovered && !titleHovered) close();
      }, 500);
      return () => clearTimeout(timer);
    }
    return;
  }, [opened, popoverHovered, titleHovered]);

  const onClickShowMatches = () => {
    setShowMatches(!showMatches);
    if (!children.length) {
      handleGetMatches({ 
        masterAccountID, 
        benchmarkYear, 
        currency, 
        job, 
        updateMatchesInState, 
        jobStateStale: true,
        setMatchDataLoading,
        displayIn
      });
    }
  };
  //Show main loader unless it's the scenario of first time user clicks to expand
  const showJobLoader = (matchDataLoading[job.id] && hasChildren) || (matchDataLoading[job.id] && !hasChildren && !showMatches);

  const onCheckDataCut = (currentMatch: INode) => {
    if (selectedMatches?.find(match => match.node.id === currentMatch.id && match.jobId === job.id)) {
      setSelectedMatches(selectedMatches.filter(match => !(match.node.id === currentMatch.id && match.jobId === job.id)));
    } else {
      setSelectedMatches([...selectedMatches, { jobId: job.id, node: currentMatch }]);
    }
  }

  const isChecked = (currentMatch: INode) => Boolean(selectedMatches?.find(match => match.node.id === currentMatch.id && match.jobId === job.id));

  return (
    <Card ref={drop} withBorder p='xl' radius='lg' shadow='sm' className={classnames({ hover_over: isOver && item.dataCuts.length, hover_over_reject: isOver && (!item.dataCuts.length || !currenciesMatch) })}>
      <Stack>
        <LoadingOverlay visible={showJobLoader} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
        <Group justify='space-between'>
          <Group>
            <Popover 
              position='bottom'
              withArrow
              shadow='xl'
              opened={opened}
            >
              <Popover.Target>
                <Title 
                  order={6} 
                  ml='5' 
                  lineClamp={1} 
                  className='org-job-title' 
                  onMouseEnter={() => {
                    setTitleHovered(true);
                    open();
                  }}
                  onMouseLeave={() => {
                    setTitleHovered(false);
                  }}
                >
                  {jobTitle || '-'}
                </Title>
              </Popover.Target>
              <Popover.Dropdown 
                maw={700} 
                onMouseEnter={() => setPopoverHovered(true)} 
                onMouseLeave={() => { 
                  setPopoverHovered(false); 
                  close(); 
                }}
              >
                <Text fw={700} size="sm">{jobTitle}</Text>
                <ScrollArea h={jobDescription?.length > 700 ? 300 : ''}>
                  <Text size="sm">{`${jobDescription || 'No Description'}`}</Text>
                </ScrollArea>
              </Popover.Dropdown>
            </Popover>
            <Divider color='dark' orientation='vertical'/>
            <Title order={6}>{jobCode || '-'}</Title>
            {useJobCodeKey && (
              <>
                <Divider color='dark' orientation='vertical'/>
                <Title order={6}>{jobCodeKey || '-'}</Title>
              </>
            )}
            <Divider color='dark' orientation='vertical'/>
            <Title order={6}>{jobCurrency || '-'}</Title>
          </Group>
          <Group>
            <Group>
              <i className='icon-circle isComposite'/>
              <Text ml='-5' fz='xs'>Composite</Text>
            </Group>
            <Group>
              <i className='icon-circle'/>
              <Text ml='-5' fz='xs'>My company</Text>
            </Group>
          </Group>
        </Group>
        <Table 
          withRowBorders={false}
          fz='xs'
        >
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => {
                return (
                  <Table.Th key={column.name + column.term}>
                      <FieldIcon column={column} hiddenFor={ ['Family', 'Grade', 'Structure'] }/>
                      {getElementShortName(column.name)}
                  </Table.Th>
                )
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              {columns.map((column) => <Table.Td key={column.name + column.term}>{formatValue(column, data, terms)}</Table.Td>)}
            </Table.Tr>
          </Table.Tbody>
        </Table>
        {/* Only show this loader when user clicks to expand */}
        {(matchDataLoading[job.id] && !hasChildren && showMatches) ? (
            <Divider label={ <Loader color='marketpayBlue' size={20} /> }/>
        ) : (
          <Divider
            label={
              <Tooltip
                label='No current matches'
                disabled={!disableShowMatches}
              >
                <Button
                onClick={onClickShowMatches}
                variant='default'
                radius='xl'
                size='compact-xs'
                disabled={disableShowMatches}
                style={{ borderColor: 'dark' }}
                fz='xs'
                fw='550'
                >
                {disableShowMatches ? 'Show 0 job matches' : showMatches ? 'Hide job matches' : `Show ${matchCount} job ${matchCount === 1 ? 'match' : 'matches'}`}
                </Button>
              </Tooltip>
            }
          />
        )}
        <DropZone 
          checkedDataCuts={checkedDataCuts}
          job={job}
          agingCode={agingCode}
          setMessages={setMessages}
          setHasDisplayedMessages={setHasDisplayedMessages}
          updateMatchesInState={updateMatchesInState}
          setMatchDataLoading={setMatchDataLoading}
          setCheckedDataCuts={setCheckedDataCuts}
        /> 
        {showMatches && hasChildren && (
          <>
            <Divider />
            { children.map((surveyJob, idx) => {
              return (
               <SurveyJob
                 key={surveyJob.id}
                 isFinalCard={idx === children.length - 1}
                 isSurveyPanel={false}
                 surveyJob={surveyJob}
                 checkedDataCuts={selectedMatches}
                 job={job}
                 currency={currency}
                 updateMatchesInState={updateMatchesInState}
                 setOrgJobsDataStale={setOrgJobsDataStale}
                 setMatchDataLoading={setMatchDataLoading}
                 onCheckDataCut={onCheckDataCut}
                 isChecked={isChecked}
               />
             )
            })}
          </>
        )}
      </Stack>
    </Card>
  )
};
